import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKidsStore } from '../../store/kidsStore';
import { useTenant } from '../../contexts/TenantContext';

export default function KidHomePage() {
  const tenant = useTenant();
  const navigate = useNavigate();
  const { selectedKid, canPlay, getStats } = useKidsStore();
  const [playStatus, setPlayStatus] = useState({
    can_play: true,
    tiempo_restante: 0,
    tiempo_jugado: 0,
    tiempo_limite: 0
  });
  const [stats, setStats] = useState({
    total_partidas: 0,
    juegos_jugados: 0
  });

  useEffect(() => {
    if (!selectedKid) {
      navigate('/kids/selector');
      return;
    }
    loadData(selectedKid.id);
  }, [selectedKid]);

  const loadData = async (kidId: number) => {
    try {
      const [playData, statsData] = await Promise.all([
        canPlay(kidId),
        getStats(kidId)
      ]);

      setPlayStatus({
        can_play: playData.can_play,
        tiempo_restante: playData.tiempo_restante || 0,
        tiempo_jugado: playData.tiempo_jugado,
        tiempo_limite: playData.tiempo_limite
      });

      setStats({
        total_partidas: statsData.total_partidas,
        juegos_jugados: statsData.juegos_jugados
      });
    } catch (error) {
      console.error('Error al cargar datos:', error);
    }
  };

  if (!selectedKid) {
    return null; // Esperando redirección
  }

  // Juegos disponibles para kids (códigos de juegos kids)
  const kidsGames = [
    { code: 'sudoku-kids', name: 'Sudoku Kids', icon: '🔢', description: 'Números y lógica', link: '/juegos/sudoku-kids' },
    { code: 'memoria', name: 'Memoria', icon: '🧩', description: 'Encuentra las parejas', link: '/juegos/memoria' },
    { code: 'simon', name: 'Simón Dice', icon: '🎵', description: 'Sigue el patrón', link: '/juegos/simon' },
    { code: 'laberinto', name: 'Laberinto', icon: '🌀', description: 'Encuentra la salida', link: '/juegos/laberinto' },
    { code: 'diferencias', name: 'Diferencias', icon: '🔍', description: 'Encuentra las diferencias', link: '/juegos/diferencias' },
    { code: 'mastermind-kids', name: 'Mastermind Kids', icon: '🎯', description: 'Adivina el código', link: '/juegos/mastermind-kids' }
  ];

  return (
    <div
      className="min-h-screen py-12 px-4"
      style={{
        background: tenant.theme.colors.gradients.main,
        fontFamily: tenant.theme.fonts.body.family
      }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Alerta de tiempo agotado */}
        {!playStatus.can_play && (
          <div
            className="mb-8 rounded-3xl p-8 flex items-center gap-6 shadow-lg"
            style={{
              background: 'linear-gradient(135deg, #FFE6E6, #FFB6C1)',
              border: `4px solid ${tenant.theme.colors.primary}`
            }}
          >
            <div className="text-6xl animate-swing">⏰</div>
            <div>
              <h2
                className="text-3xl font-bold mb-2"
                style={{ color: tenant.theme.colors.primary }}
              >
                ¡Tiempo de juego terminado por hoy!
              </h2>
              <p className="text-gray-700 text-lg">
                Has jugado {playStatus.tiempo_jugado} minutos. ¡Vuelve mañana para más diversión!
              </p>
              <p className="text-gray-700">
                Mientras tanto, puedes jugar afuera, leer un libro o hacer tus tareas. 😊
              </p>
            </div>
          </div>
        )}

        {/* Bienvenida */}
        <div className="text-center mb-12">
          <h1
            className="text-6xl font-bold text-white mb-4 animate-bounce"
            style={{
              fontFamily: tenant.theme.fonts.primary.family,
              textShadow: '4px 4px 0 rgba(0,0,0,0.2)'
            }}
          >
            ¡Hola, {selectedKid.nombre}!
          </h1>
          <p
            className="text-3xl text-white font-semibold"
            style={{ textShadow: '2px 2px 0 rgba(0,0,0,0.2)' }}
          >
            ¿Listo para entrenar tu cerebro? 🧠✨
          </p>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
            <div className="text-5xl mb-3">🎮</div>
            <div
              className="text-4xl font-bold mb-2"
              style={{ color: tenant.theme.colors.primary }}
            >
              {stats.total_partidas}
            </div>
            <div className="text-gray-600 font-semibold">Partidas Jugadas</div>
          </div>
          <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
            <div className="text-5xl mb-3">🎯</div>
            <div
              className="text-4xl font-bold mb-2"
              style={{ color: tenant.theme.colors.primary }}
            >
              {stats.juegos_jugados}
            </div>
            <div className="text-gray-600 font-semibold">Juegos Diferentes</div>
          </div>
          <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
            <div className="text-5xl mb-3">⭐</div>
            <div
              className="text-4xl font-bold mb-2"
              style={{ color: tenant.theme.colors.primary }}
            >
              {playStatus.tiempo_restante}
            </div>
            <div className="text-gray-600 font-semibold">Minutos Disponibles</div>
          </div>
        </div>

        {/* Sección de juegos */}
        <div className="mb-12">
          <h2
            className="text-5xl font-bold text-white text-center mb-8"
            style={{
              fontFamily: tenant.theme.fonts.primary.family,
              textShadow: '3px 3px 0 rgba(0,0,0,0.2)'
            }}
          >
            🎮 Elige tu Juego
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {kidsGames.map((game) => (
              <a
                key={game.code}
                href={playStatus.can_play ? game.link : '#'}
                className={`
                  bg-white rounded-3xl p-8 text-center shadow-lg transition-all
                  ${playStatus.can_play
                    ? 'hover:scale-105 hover:shadow-2xl cursor-pointer'
                    : 'opacity-50 cursor-not-allowed'
                  }
                `}
                onClick={(e) => {
                  if (!playStatus.can_play) {
                    e.preventDefault();
                  }
                }}
              >
                <div className="text-7xl mb-4 transition-transform hover:scale-125 hover:rotate-12">
                  {game.icon}
                </div>
                <h3
                  className="text-2xl font-bold mb-2"
                  style={{ color: tenant.theme.colors.primary }}
                >
                  {game.name}
                </h3>
                <p className="text-gray-600 font-semibold">{game.description}</p>
              </a>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes swing {
          0%, 100% { transform: rotate(-10deg); }
          50% { transform: rotate(10deg); }
        }

        .animate-swing {
          animation: swing 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
